import React from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { Link } from 'react-router-dom';

const PerformanceFrameworkStage2 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 2: Assess Your Building Blocks" icon={<WrenchScrewdriverIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <ul className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Assess whether you have the foundational building blocks in place for effective performance practices</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Identify gaps in role clarity, goal setting, values/behaviours, and right people in right roles</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Prioritize which foundational elements to strengthen before building your framework</span>
            </li>
          </ul>
          <p className="mt-4 text-gray-300">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">The Foundation Checklist</h3>
        <p className="text-gray-300 mb-6">
          Genuinely effective performance practices are built on solid foundations. Before jumping into rating systems 
          and performance tools, assess whether you have these building blocks in place.
        </p>

        <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700 mb-6">
          <h4 className="text-white font-semibold mb-4">Quick Foundation Assessment:</h4>
          <div className="space-y-4">
            <div className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-5 w-5 text-primary-light" />
              <span className="text-gray-300">Do we have the right people in the right roles?</span>
            </div>
            <div className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-5 w-5 text-primary-light" />
              <span className="text-gray-300">Do those people have clarity on roles & responsibilities?</span>
            </div>
            <div className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-5 w-5 text-primary-light" />
              <span className="text-gray-300">Is there clarity on goals & ways of work?</span>
            </div>
            <div className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-5 w-5 text-primary-light" />
              <span className="text-gray-300">Is there clarity on values and expected behaviours?</span>
            </div>
          </div>
        </div>

        <div className="mt-6 p-8 bg-gray-800/50 rounded-lg border border-gray-700 mb-6">
          <div className="flex flex-col items-center space-y-6">
            <div className="text-center">
              <h5 className="text-white font-semibold text-lg mb-4">Performance Framework Formula</h5>
            </div>
            
            <div className="flex items-start justify-center space-x-4">
              <div className="flex flex-col items-center space-y-3">
                <div className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold text-base h-10 flex items-center">
                  Results
                </div>
                <div className="text-center">
                  <div className="text-gray-400 text-sm font-medium">What we deliver</div>
                  <div className="text-gray-300 text-xs mt-1">relative to expectations & goals</div>
                </div>
              </div>
              
              <div className="flex items-center h-10 mt-0">
                <span className="text-primary-light font-bold text-xl">+</span>
              </div>
              
              <div className="flex flex-col items-center space-y-3">
                <div className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold text-base h-10 flex items-center">
                  Execution
                </div>
                <div className="text-center">
                  <div className="text-gray-400 text-sm font-medium">How effectively we deliver</div>
                  <div className="text-gray-300 text-xs mt-1">in context of expected competencies</div>
                </div>
              </div>
              
              <div className="flex items-center h-10 mt-0">
                <span className="text-primary-light font-bold text-xl">+</span>
              </div>
              
              <div className="flex flex-col items-center space-y-3">
                <div className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold text-base h-10 flex items-center">
                  Behaviours
                </div>
                <div className="text-center">
                  <div className="text-gray-400 text-sm font-medium">How we role-model</div>
                  <div className="text-gray-300 text-xs mt-1">expected behaviours day to day</div>
                </div>
              </div>
              
              <div className="flex items-center h-10 mt-0">
                <span className="text-primary-light font-bold text-xl">=</span>
              </div>
              
              <div className="flex items-center">
                <div className="bg-primary text-white px-6 py-3 rounded-lg font-bold text-base h-12 flex items-center">
                  Performance
                </div>
              </div>
            </div>
            
                          {/* Attribution */}
              <div className="text-center mt-6">
                <img 
                  src="https://auth.openorg.fyi/storage/v1/object/public/web-assets/oo-logo/Default%20Transparent%20PNG.png" 
                  alt="Open Org Logo" 
                  className="h-4 opacity-60 mx-auto"
                />
              </div>
          </div>
        </div>

        <h4 className="text-lg font-semibold text-white mb-3">The Three Core Components:</h4>
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h5 className="text-white font-semibold mb-2">1. Results</h5>
            <p className="text-gray-300 text-sm">What we deliver, relative to expectations & goals</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h5 className="text-white font-semibold mb-2">2. Execution</h5>
            <p className="text-gray-300 text-sm">How effectively & independently we deliver it, in the context of expected competencies</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h5 className="text-white font-semibold mb-2">3. Behaviours</h5>
            <p className="text-gray-300 text-sm">How well we meet & role-model expected behaviours day to day</p>
          </div>
        </div>

        <p className="text-gray-300 text-sm italic">
          If you're missing pieces from the foundation checklist, we'd strongly advise starting there first.
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Building Block #1: Right People, Right Roles</h3>
        <p className="text-gray-300 mb-4">
          This is a longer-term ongoing item, but it's important to assess current state.
        </p>

        <h4 className="text-lg font-semibold text-white mb-3">You Need:</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Alignment on organizational design</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Alignment on 'who thrives here' (linked to clarity on ways of work)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Clear, honest employer brand to attract & repel the right talent</span>
          </li>
        </ul>

        <ExpandableCallout type="tip" title="Quick MVP: The '1-Line Role Summary' Exercise" className="mb-4">
          <p className="text-gray-300 mb-4">
            Create a lightweight doc that maps each person's current role in one sentence:
          </p>
          <div className="bg-gray-800 p-4 rounded border border-gray-700 mb-4">
            <p className="text-gray-300 font-mono text-sm">
              "I own [X] and am responsible for [Y outcomes]."
            </p>
          </div>
          <p className="text-gray-300 mb-3">Then add:</p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Their team/job family (e.g., Sales, Product)</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Level (rough proxy is fine: junior/mid/senior/lead)</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Link to their most recent job description (if it exists)</span>
            </li>
          </ul>
        </ExpandableCallout>

        <ExpandableCallout type="info" title="Advanced Approach: Full Competency Framework">
          <div className="space-y-4">
            <p className="text-gray-300">For a more comprehensive approach, consider building a full competency framework:</p>
            
            <div className="space-y-3">
              <h5 className="text-white font-medium">What this includes:</h5>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-light mr-3 font-bold">—</span>
                  <span className="text-gray-300">Detailed role profiles for each position</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3 font-bold">—</span>
                  <span className="text-gray-300">Clear progression pathways between levels</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3 font-bold">—</span>
                  <span className="text-gray-300">Specific competencies and expectations for each level</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3 font-bold">—</span>
                  <span className="text-gray-300">Salary bands aligned to levels</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-gray-300 text-sm">
                <strong className="text-white">Time investment:</strong> 6-12 weeks for comprehensive framework vs 1-2 weeks for MVP approach
              </p>
            </div>
          </div>
        </ExpandableCallout>

        <CopyToNotion title="Role Summary Map Template">
          <div className="space-y-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-white font-semibold py-2">Name</th>
                  <th className="text-left text-white font-semibold py-2">Role Summary</th>
                  <th className="text-left text-white font-semibold py-2">Team/Function</th>
                  <th className="text-left text-white font-semibold py-2">Level</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-800">
                  <td className="py-2 text-gray-300">Sarah Johnson</td>
                  <td className="py-2 text-gray-300">I own product discovery and am responsible for quarterly feature delivery</td>
                  <td className="py-2 text-gray-300">Product</td>
                  <td className="py-2 text-gray-300">Senior</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-2 text-gray-300">Mike Chen</td>
                  <td className="py-2 text-gray-300">I own enterprise sales and am responsible for $2M ARR target</td>
                  <td className="py-2 text-gray-300">Sales</td>
                  <td className="py-2 text-gray-300">Mid</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CopyToNotion>

        <div className="mt-4">
          <ToolCallout
            tool="openai"
            label="Competency Framework Builder (GPT Tool)"
            href="https://www.openverse.fyi/resource/competency-and-career-framework-designer-gpt-tool"
            description="AI tool to help accelerate building a basic competency framework V1"
          />
        </div>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Building Block #2: Clarity on Goals & Ways of Work</h3>
        
        <h4 className="text-lg font-semibold text-white mb-3">You Need:</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">At least a lightweight goal-setting framework that the whole business can align to</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">3-5 clear business goals (from exec team) communicated to the whole business</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Team & individual goals that connect to business impact</span>
          </li>
        </ul>

        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6">
          <p className="text-gray-300 text-sm">
            <strong className="text-white">Bottom Line:</strong> Everyone should understand exactly how they can and should be 
            impacting business goals & performance, and be able to directly link their day-to-day work to business impact.
          </p>
        </div>

        <ExpandableCallout type="tip" title="Culture & Ways of Work Assessment Tools" className="mb-4">
          <div className="space-y-4">
            <div>
              <h5 className="text-white font-medium mb-2">Quick Culture Diagnostic:</h5>
              <p className="text-gray-300 text-sm mb-2">
                5-minute personal diagnostic to surface areas across your culture & ways of work that need attention.
              </p>
            </div>
            <div>
              <h5 className="text-white font-medium mb-2">Culture Design Toolkit:</h5>
              <p className="text-gray-300 text-sm">
                Deeper exercise to run with leadership team to spot areas needing attention and bring clarity to your ways of working.
              </p>
            </div>
          </div>
        </ExpandableCallout>

        <div className="mt-4 space-y-4">
          <ToolCallout
            tool="notion"
            label="Open Org's Culture Heat-map"
            href="https://www.notion.so/open-org/11ff015e5d4680fb9d4afce35ec36ac8?v=11ff015e5d468125bfbb000ccece2596&source=copy_link"
            description="Great as a quick ~5min personal diagnostic to help you surface areas across your culture & ways of work that need attention"
          />
          
          <ToolCallout
            tool="miro"
            label="Open Org Culture Design Toolkit"
            href="https://miro.com/miroverse/open-org-culture-design-toolkit/?social=copy-link"
            description="Comprehensive toolkit for assessing and designing organizational culture"
          />
        </div>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Building Block #3: Clarity on Values & Behaviours</h3>
        
        <p className="text-gray-300 mb-6">
          Performance assessment must not be grounded only in what someone achieves, but the way they achieve it. 
          It's very hard to do this fairly if your values & behaviours are vague and undefined.
        </p>

        <h4 className="text-lg font-semibold text-white mb-3">You Need:</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Clear values & linked behaviours (Do's & Don'ts) that everyone is aligned on</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Documented examples of what good and poor behaviour looks like</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Clear consequences for not meeting behavioural expectations</span>
          </li>
        </ul>

        <PlaybookImage 
          src="https://auth.openorg.fyi/storage/v1/object/public/resources/performance/runa0values.png"
          alt="Example of Runa's values from their handbook showing clear behavioral expectations"
          className="mb-6 rounded-lg border border-gray-700"
          caption="Example of Runa's values from their handbook showing clear behavioral expectations"
        />

        <div className="mt-4">
          <ToolCallout
            label="7-Step Values Design and Evolution Blueprint"
            href="https://www.openverse.fyi/resource/7-step-values-design-and-evolution-blueprint"
            description="Complete blueprint for designing and embedding organizational values"
          />
        </div>
      </PlaybookCard>

      <ExpandableCallout type="info" title="Frequently Asked Questions" defaultOpen={false}>
        <div className="space-y-6">
          <div>
            <h5 className="text-white font-medium mb-2">Q: We're a 20-person startup. Do we really need all these building blocks?</h5>
            <p className="text-gray-300 text-sm">
              <strong>A:</strong> No! Start with the MVP approach: basic role clarity (who does what), simple goals alignment, and clear behavioral expectations. 
              You can build the sophisticated stuff later as you grow.
            </p>
          </div>
          
          <div>
            <h5 className="text-white font-medium mb-2">Q: What if we have some building blocks but they're inconsistent across teams?</h5>
            <p className="text-gray-300 text-sm">
              <strong>A:</strong> Start by documenting what exists, then standardize the most critical elements first. 
              It's better to have consistent basics than inconsistent sophistication.
            </p>
          </div>
          
          <div>
            <h5 className="text-white font-medium mb-2">Q: Our leadership team disagrees on values and behaviors. How do we move forward?</h5>
            <p className="text-gray-300 text-sm">
              <strong>A:</strong> Pause the performance framework work and solve this first. Consider running a values workshop or bringing in facilitation. 
              Performance frameworks built on shaky cultural foundations will fail.
            </p>
          </div>
          
          <div>
            <h5 className="text-white font-medium mb-2">Q: Can we build a performance framework while fixing these building blocks?</h5>
            <p className="text-gray-300 text-sm">
              <strong>A:</strong> Yes, but be explicit about it. You might build a "Version 1" framework with current building blocks, 
              then upgrade to "Version 2" once foundations are stronger. Just don't pretend they're perfect from day one.
            </p>
          </div>
          
          <div>
            <h5 className="text-white font-medium mb-2">Q: How do we know if our building blocks are "good enough" to proceed?</h5>
            <p className="text-gray-300 text-sm">
              <strong>A:</strong> Ask yourself: Can a manager explain what "good performance" looks like for each role? 
              Can employees understand how their work connects to business goals? If yes, you can proceed. If no, you need more foundation work.
            </p>
          </div>
        </div>
      </ExpandableCallout>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Outcome</h3>
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <h4 className="text-white font-semibold mb-3">By the end of this stage you should have:</h4>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Clarity on the building blocks needed for effective performance practices</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">A prioritized list of foundational elements to strengthen (if any gaps exist)</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Basic role clarity, goal alignment, and behavioral expectations in place</span>
            </li>
          </ul>
          <div className="mt-4 pt-4 border-t border-primary/30">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Next:</strong> Move to <Link to="/playbooks/how-to-build-launch-embed-performance/stage-3" className="text-primary hover:text-primary-light">Stage 3: Agree & Align on How We'll Use Performance</Link> 
              to define how performance will influence growth, development, and reward.
            </p>
          </div>
        </div>
      </PlaybookCard>

      <ExpandableCallout type="warning" title="Where You Might Face Resistance" defaultOpen={false}>
        <div className="space-y-4">
          <p className="text-gray-300">
            This foundational work can feel burdensome to some who want to focus on 'building the thing' rather than 
            establishing building blocks. However, these foundations are vital for any performance framework that works well.
          </p>
          <p className="text-gray-300">
            Focus on what your specific components and framework need. If your exec team doesn't want to factor behaviours 
            into performance assessment, that might be an area you park for the future.
          </p>
          <div className="bg-gray-800/50 p-4 rounded-lg border-l-4 border-primary-light">
            <p className="text-gray-300">
              <strong className="text-white">Pro tip:</strong> Keep it simple, but not at the expense of clarity. 
              You can always build on these foundations over time.
            </p>
          </div>
        </div>
      </ExpandableCallout>
    </PlaybookSection>
  </div>
);

export default PerformanceFrameworkStage2; 